import React, { memo, useRef } from 'react';
import { ArrowRight, Repeat12, X } from 'shared/assets/icons';
import { Checkbox, Colors } from 'shared/components/ui/checkbox';
import { Icon } from 'shared/components/ui/icon';
import { useToday } from 'shared/contexts/today';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { ScheduleEntryState } from 'shared/types/habit-schedule';
import { TaskCheckOptions } from 'shared/types/task';
import { TaskPriorityOptions, TaskType } from 'shared/types/task-base';
import { Timestamp } from 'shared/types/timestamp';
import { isNumber } from 'shared/utils/is-number';

import { CheckboxContextMenu } from './checkbox-context-menu';
import { OverdueContextMenu } from './overdue-context-menu';
import * as Styled from './task-detail-checkbox.style';
import { IconColors } from './types';

const priorityCheckboxColorMap: Record<TaskPriorityOptions, Colors> = {
  [TaskPriorityOptions.Low]: Colors.Info,
  [TaskPriorityOptions.Medium]: Colors.Warning,
  [TaskPriorityOptions.High]: Colors.Error,
  [TaskPriorityOptions.None]: Colors.Default,
};

const priorityIconColorMap: Record<TaskPriorityOptions, IconColors> = {
  [TaskPriorityOptions.Low]: IconColors.Info,
  [TaskPriorityOptions.Medium]: IconColors.Warning,
  [TaskPriorityOptions.High]: IconColors.Error,
  [TaskPriorityOptions.None]: IconColors.Default,
};

export type TaskDetailCheckboxProps = {
  onChange: (date: Timestamp) => void;
  checked: TaskCheckOptions;
  name: string;
  type: TaskType;
  overdueDate?: Timestamp | null;
  onContextChange?: (
    type: ScheduleEntryState,
    options: { value?: number; increment?: number; date: Timestamp },
  ) => void;
  onContextManual?: (date: Timestamp) => void;
  completionPercentage?: number;
  target?: number | null;
  priority?: TaskPriorityOptions | null;
  color?: string;
  icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  isFrozen?: boolean;
  isDisabled?: boolean;
  isSmall?: boolean;
  overrideChecked?: TaskCheckOptions;
  onToggle?: () => void;
};

export const TaskDetailCheckbox: React.FC<TaskDetailCheckboxProps> = memo(
  ({
    onChange,
    checked,
    name,
    type,
    overdueDate,
    onContextChange,
    onContextManual,
    completionPercentage,
    target,
    priority,
    color,
    icon = Repeat12,
    isFrozen,
    isDisabled,
    isSmall,
    overrideChecked,
    onToggle,
  }) => {
    const today = useToday();
    const containerRef = useRef<HTMLSpanElement>(null);
    const { menuOpen, openMenu, closeMenu } = useOpenMenu();
    const {
      menuOpen: overdueMenuOpen,
      openMenu: openOverdueMenu,
      closeMenu: closeOverdueMenu,
    } = useOpenMenu();

    const onContextMenu = (e: React.MouseEvent) => {
      if (!isNumber(completionPercentage) || !onContextChange) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      openMenu();
    };

    const _onChange = () => {
      if (onToggle) {
        onToggle();
        return;
      }
      if (isFrozen) {
        return;
      }
      if (type !== TaskType.Task && overdueDate) {
        openOverdueMenu();
        return;
      }

      onChange(today);
    };

    const preventPropagation = (e: React.MouseEvent) => {
      e.stopPropagation();
    };

    const finalChecked = overrideChecked ?? checked;

    return (
      <Styled.CheckboxContainer
        $isFaded={isFrozen || isDisabled}
        onClick={preventPropagation}
        ref={containerRef}
        onContextMenu={onContextMenu}
      >
        {[
          TaskCheckOptions.Unchecked,
          TaskCheckOptions.Checked,
          TaskCheckOptions.FailedChecked,
          TaskCheckOptions.SkippedChecked,
        ].includes(finalChecked) ? (
          <Checkbox
            checked={finalChecked !== TaskCheckOptions.Unchecked}
            completionPercentage={completionPercentage}
            color={
              finalChecked === TaskCheckOptions.FailedChecked
                ? Colors.Error
                : finalChecked === TaskCheckOptions.SkippedChecked
                  ? Colors.Warning
                  : color ??
                    (priority ? priorityCheckboxColorMap[priority] : undefined)
            }
            keepColorOnChecked={
              finalChecked === TaskCheckOptions.FailedChecked ||
              finalChecked === TaskCheckOptions.SkippedChecked
            }
            checkIcon={
              finalChecked === TaskCheckOptions.FailedChecked
                ? X
                : finalChecked === TaskCheckOptions.SkippedChecked
                  ? ArrowRight
                  : undefined
            }
            onChange={_onChange}
            disabled={isDisabled}
            isSmall={isSmall}
          />
        ) : (
          <Styled.IconContainer
            $color={
              priority
                ? priorityIconColorMap[priority]
                : priorityIconColorMap[TaskPriorityOptions.None]
            }
          >
            <Icon icon={icon} />
          </Styled.IconContainer>
        )}

        {menuOpen && !!onContextChange && (
          <CheckboxContextMenu
            name={name}
            target={target}
            overdueDate={overdueDate}
            referenceElement={containerRef}
            onClose={closeMenu}
            onOption={onContextChange}
            onManual={onContextManual}
          />
        )}

        {overdueMenuOpen && type !== TaskType.Task && !!overdueDate && (
          <OverdueContextMenu
            name={name}
            overdueDate={overdueDate}
            onDate={onChange}
            onClose={closeOverdueMenu}
            referenceElement={containerRef}
          />
        )}
      </Styled.CheckboxContainer>
    );
  },
);
